import { useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { useAuthStore } from '@/stores/authStore';

// Refresh token 2 minutes before expiry
const REFRESH_BUFFER_MS = 2 * 60 * 1000;
// Minimum time between refresh attempts (prevent flood)
const MIN_REFRESH_INTERVAL_MS = 30 * 1000; // 30 seconds

function parseJwt(token: string): { exp: number } | null {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch {
    return null;
  }
}

export function useTokenRefresh() {
  const timeoutRef = useRef<NodeJS.Timeout>();
  const isRefreshingRef = useRef(false);
  const lastRefreshAttemptRef = useRef<number>(0);
  const { accessToken, refreshToken, setAccessToken, isAuthenticated, logout } = useAuthStore();

  const doRefresh = useCallback(async (): Promise<boolean> => {
    // Prevent rapid-fire refresh attempts
    const now = Date.now();
    const timeSinceLastAttempt = now - lastRefreshAttemptRef.current;
    if (timeSinceLastAttempt < MIN_REFRESH_INTERVAL_MS) {
      console.log(`[TokenRefresh] Throttled - only ${Math.round(timeSinceLastAttempt / 1000)}s since last attempt`);
      return false;
    }

    if (isRefreshingRef.current || !refreshToken) return false;
    isRefreshingRef.current = true;
    lastRefreshAttemptRef.current = now;

    const API_URL = import.meta.env.VITE_API_URL || '/api';

    try {
      console.log('[TokenRefresh] Refreshing token...');
      const response = await axios.post(`${API_URL}/auth/refresh`, { refreshToken });
      const { accessToken: newToken } = response.data;
      setAccessToken(newToken);
      console.log('[TokenRefresh] Token refreshed successfully');
      return true; // Success - caller should reschedule
    } catch (error: any) {
      console.error('[TokenRefresh] Failed:', error?.response?.status || error.message);
      if (error?.response?.status === 401 || error?.response?.status === 403) {
        console.log('[TokenRefresh] Refresh token rejected, logging out');
        logout();
        window.location.href = '/login';
      }
      // On ANY error (including 429), don't reschedule here - let the caller handle it
      // This prevents double-scheduling
      return false;
    } finally {
      isRefreshingRef.current = false;
    }
  }, [refreshToken, setAccessToken, logout]);

  useEffect(() => {
    if (!isAuthenticated || !accessToken || !refreshToken) {
      return;
    }

    const scheduleRefresh = () => {
      // Clear any existing timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      const payload = parseJwt(accessToken);
      if (!payload?.exp) return;

      const expiryMs = payload.exp * 1000;
      const now = Date.now();
      const timeUntilRefresh = expiryMs - now - REFRESH_BUFFER_MS;

      // If token is already expired or about to expire, refresh immediately
      if (timeUntilRefresh <= 0) {
        console.log('[TokenRefresh] Token expired/expiring, refreshing now');
        doRefresh().then((success) => {
          if (!success) {
            // Failed - schedule retry in 30s (throttle will also prevent flood)
            console.log('[TokenRefresh] Failed, scheduling retry in 30s');
            timeoutRef.current = setTimeout(scheduleRefresh, 30000);
          }
          // On success, useEffect will re-run with new accessToken and reschedule
        });
        return;
      }

      // Schedule refresh for later
      console.log(`[TokenRefresh] Scheduled in ${Math.round(timeUntilRefresh / 1000)}s`);
      timeoutRef.current = setTimeout(async () => {
        const success = await doRefresh();
        if (!success) {
          // Failed - schedule retry in 30s
          console.log('[TokenRefresh] Failed, scheduling retry in 30s');
          timeoutRef.current = setTimeout(scheduleRefresh, 30000);
        }
        // On success, useEffect will re-run with new accessToken and reschedule
      }, timeUntilRefresh);
    };

    scheduleRefresh();

    // Check on visibility change (setTimeout doesn't run reliably when tab is backgrounded)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        console.log('[TokenRefresh] Tab visible, checking token');
        // Just call scheduleRefresh - it will check expiry and act accordingly
        // The throttle in doRefresh prevents flooding
        scheduleRefresh();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isAuthenticated, accessToken, refreshToken, doRefresh]);
}
